import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Content } from '@/components/cardWithThreeFooterBox';
import { GridWidget } from '@/components/GridWidget.tsx';
import { fromApiDateTime, Icon } from '@/utils';

import {
  BoxPlotCategory,
  BoxPlotChartConfigType,
  ChartInterval,
  ChartProps,
  ChartType,
  ICardDataState,
  PlotType,
} from '../types.ts';
import { BoxPlotHighChart } from './boxPlotCard/BoxPlotHighChart.tsx';

/**
 * Set config or use localStorage via useChartStorage
 * @param id
 * @param onOpenModal
 * @param onRemove
 * @param config
 * @param hideEditChart
 * @constructor
 */
export const BoxPlotCard = ({
  id,
  onOpenModal,

  hideEditChart,
  config,
  ...rest
}: ChartProps<BoxPlotChartConfigType>) => {
  const { t } = useTranslation(['common', 'chartConfig']);

  const [chartDataStat, setChartDataStat] = useState<ICardDataState>({
    isLoading: true,
    isError: false,
    isEmpty: false,
  });

  const [chartName, setChartName] = useState<string | undefined>(undefined);

  const chartConfig = useMemo<BoxPlotChartConfigType>(() => {
    const cfg: BoxPlotChartConfigType = {
      ...defaultConfig,
      ...config,
    };

    // Ensure datetimes are local DateTimes (API strings may include an offset).
    cfg.boxes.forEach(box => {
      if (typeof box.start === 'string') box.start = fromApiDateTime(box.start);
      if (typeof box.end === 'string') box.end = fromApiDateTime(box.end);
    });

    return cfg;
  }, [config]);

  return (
    <GridWidget
      id={id}
      headerTitle={chartConfig?.title || ''}
      headerSubtitle={chartName}
      {...chartDataStat}
      emptyMessages={[t('chartConfig:noReadings'), t('chartConfig:noReadingsMessage')]}
      {...rest}
      toolbarActionList={
        hideEditChart
          ? undefined
          : [
              {
                id: uuidv4(),
                label: t('common:edit'),
                onClick: onOpenModal,
                Icon: Icon.SlidersHorizontal,
              },
            ]
      }
    >
      <Content>
        <BoxPlotHighChart
          config={chartConfig}
          setChartDataStat={setChartDataStat}
          onChartNameChange={setChartName}
        />
      </Content>
    </GridWidget>
  );
};

// ************************************
// Constants
// ************************************
const defaultConfig: BoxPlotChartConfigType = {
  plotType: PlotType.BOXPLOT,
  title: '',
  category: BoxPlotCategory.Day,
  boxes: [],
  type: ChartType.LIVE,
  interval: ChartInterval.Week,
};
